/**
 * Unified Optimized Image component with robust Supabase support
 * This component replaces all other image components for consistency
 */
'use client'

import Image from 'next/image'
import { useState, useEffect, useCallback } from 'react'
import { getCache, setCache, CACHE_TTLS } from '@/lib/global-cache'
import { getBlurDataURL } from '@/lib/blur-data-url'
import { 
  isSupabaseStorageUrl, 
  DeviceCapabilityDetector 
} from '@/lib/image-optimization'

interface OptimizedImageProps {
  src: string | null | undefined
  alt: string
  width?: number
  height?: number
  className?: string
  priority?: boolean
  sizes?: string
  fill?: boolean
  quality?: number
  placeholder?: 'blur' | 'empty'
  blurDataURL?: string
  onLoad?: () => void
  onError?: () => void
  // Enhanced props for better control
  retryAttempts?: number
  retryDelay?: number
  fallbackToOriginal?: boolean
}

interface ImageState {
  currentSrc: string | null
  fallbackUrls: string[]
  currentUrlIndex: number
  isLoading: boolean
  hasError: boolean
  retryCount: number
}

export default function OptimizedImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  sizes,
  fill = false,
  quality = 85,
  placeholder = 'empty',
  blurDataURL,
  onLoad,
  onError,
  retryAttempts = 3,
  retryDelay = 1000,
  fallbackToOriginal = true
}: OptimizedImageProps) {
  const [imageState, setImageState] = useState<ImageState>({
    currentSrc: null,
    fallbackUrls: [],
    currentUrlIndex: 0,
    isLoading: true,
    hasError: false,
    retryCount: 0
  })

  // Initialize image URLs and handle different image types
  const initializeImage = useCallback(async () => {
    if (!src || typeof src !== 'string') {
      setImageState(prev => ({ ...prev, hasError: true, isLoading: false }))
      return
    }

    // Check cache first
    const cacheKey = `unified-img:${src}:${width}x${height}:${quality}`
    const cachedResult = getCache<{ url: string; fallbacks: string[] }>(cacheKey)
    
    if (cachedResult) {
      setImageState({
        currentSrc: cachedResult.url,
        fallbackUrls: cachedResult.fallbacks,
        currentUrlIndex: 0,
        isLoading: false,
        hasError: false,
        retryCount: 0
      })
      return
    }

    // For local images, use directly
    if (src.startsWith('/')) {
      const result = { url: src, fallbacks: [src] }
      setCache(cacheKey, result, CACHE_TTLS.IMAGES)
      setImageState({
        currentSrc: src,
        fallbackUrls: [src],
        currentUrlIndex: 0,
        isLoading: false,
        hasError: false,
        retryCount: 0
      })
      return
    }

    // For Supabase images, generate optimized URLs with fallbacks
    if (isSupabaseStorageUrl(src)) {
      try {
        const detector = DeviceCapabilityDetector.getInstance()
        await detector.detectCapabilities()
        
        const optimalFormat = detector.getOptimalFormat()
        const optimalQuality = quality || detector.getOptimalQuality(priority)

        // Use self-hosted optimization instead of Vercel
        const { generateSelfHostedImageUrl } = await import('@/lib/self-hosted-image-optimization')
        
        const optimizedUrl = generateSelfHostedImageUrl(src, {
          width,
          height,
          quality: optimalQuality,
          format: optimalFormat
        })

        const fallbackUrls = [optimizedUrl]
        
        // Add original URL as final fallback if enabled
        if (fallbackToOriginal && !fallbackUrls.includes(src)) {
          fallbackUrls.push(src)
        }

        const result = { url: fallbackUrls[0], fallbacks: fallbackUrls }
        setCache(cacheKey, result, CACHE_TTLS.IMAGES)
        
        setImageState({
          currentSrc: fallbackUrls[0],
          fallbackUrls,
          currentUrlIndex: 0,
          isLoading: false,
          hasError: false,
          retryCount: 0
        })
      } catch (error) {
        console.error('Failed to initialize Supabase image:', error)
        setImageState(prev => ({ ...prev, hasError: true, isLoading: false }))
      }
      return
    }

    // For other external images, use directly
    setImageState({
      currentSrc: src,
      fallbackUrls: [src],
      currentUrlIndex: 0,
      isLoading: false,
      hasError: false,
      retryCount: 0
    })
  }, [src, width, height, quality, priority, fallbackToOriginal])

  // Handle image load errors with intelligent fallback
  const handleImageError = useCallback(async () => {
    const { fallbackUrls, currentUrlIndex, retryCount } = imageState

    // Try next fallback URL
    const nextIndex = currentUrlIndex + 1
    if (nextIndex < fallbackUrls.length) {
      console.warn(`Image failed: ${imageState.currentSrc}, trying fallback ${nextIndex}/${fallbackUrls.length}`)
      
      setImageState(prev => ({
        ...prev,
        currentSrc: fallbackUrls[nextIndex],
        currentUrlIndex: nextIndex,
        retryCount: 0
      }))
      return
    }

    // If we've exhausted fallbacks, try retrying the current URL
    if (retryCount < retryAttempts) {
      console.warn(`Retrying image load (${retryCount + 1}/${retryAttempts}): ${imageState.currentSrc}`)
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, retryDelay))
      
      setImageState(prev => ({
        ...prev,
        retryCount: prev.retryCount + 1
      }))
      
      // Force re-render by updating the src slightly
      const currentSrc = imageState.currentSrc
      if (currentSrc) {
        const separator = currentSrc.includes('?') ? '&' : '?'
        const retryUrl = `${currentSrc}${separator}_retry=${Date.now()}`
        setImageState(prev => ({
          ...prev,
          currentSrc: retryUrl
        }))
      }
      return
    }

    // All attempts failed
    console.error(`All image URLs and retries failed for: ${src}`)
    setImageState(prev => ({ ...prev, hasError: true }))
    onError?.()
  }, [imageState, retryAttempts, retryDelay, src, onError])

  // Handle successful image load
  const handleImageLoad = useCallback(() => {
    setImageState(prev => ({ ...prev, isLoading: false }))
    onLoad?.()
  }, [onLoad])

  // Initialize image on mount or src change
  useEffect(() => {
    initializeImage()
  }, [initializeImage])

  // Loading state
  if (imageState.isLoading || !imageState.currentSrc) {
    const containerStyle = fill 
      ? { position: 'relative' as const, width: '100%', height: '100%' }
      : { width, height }

    return (
      <div 
        className={`bg-gray-100/50 animate-pulse flex items-center justify-center ${className}`}
        style={containerStyle}
        role="img"
        aria-label={`Loading ${alt}`}
      >
        <svg 
          className="w-6 h-6 text-gray-300" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5} 
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
          />
        </svg>
      </div>
    )
  }

  // Error state
  if (imageState.hasError) {
    const containerStyle = fill 
      ? { position: 'relative' as const, width: '100%', height: '100%' }
      : { width, height }

    return (
      <div 
        className={`bg-gray-50/50 flex items-center justify-center ${className}`}
        style={containerStyle}
        role="img"
        aria-label={`Failed to load ${alt}`}
      >
        <svg 
          className="w-6 h-6 text-gray-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5} 
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" 
          />
        </svg>
      </div>
    )
  }

  // Determine if we should use unoptimized mode
  // Use unoptimized for local images to prevent 402 errors from Next.js optimization
  const shouldUseUnoptimized = ( (imageState.currentSrc && typeof imageState.currentSrc === 'string') && (
    imageState.currentSrc.startsWith('/api/image-proxy') || 
    imageState.currentSrc.startsWith('/') ||
    imageState.currentSrc.endsWith('.svg') ) || false
  )

  // Render optimized image
  // Since we disabled Vercel's image optimization globally, we handle optimization ourselves
  return (
    <Image
      src={imageState.currentSrc}
      alt={alt}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      fill={fill}
      className={className}
      priority={priority}
      sizes={sizes}
      quality={quality}
      placeholder={placeholder}
      blurDataURL={blurDataURL || getBlurDataURL(placeholder, width, height)}
      onLoad={handleImageLoad}
      onError={handleImageError}
      // Always unoptimized since we handle optimization via our proxy
      unoptimized={shouldUseUnoptimized}
    />
  )
}

/**
 * Preload images for better performance
 */
export function preloadImages(urls: string[]): Promise<void[]> {
  return Promise.all(
    urls.map(url => 
      new Promise<void>((resolve, reject) => {
        const img = new window.Image()
        img.onload = () => {
          // Cache successful loads
          setCache(`image:${url}`, url, CACHE_TTLS.IMAGES)
          resolve()
        }
        img.onerror = reject
        img.src = url
      })
    )
  )
}